import { GUIDE_SAVE_VERSION, type GuideCreateOptions, type GuideLegacySave, type GuideSaveJson } from "./guide";

/**
 * The guide's save (SaveRecord.guide) and the one-time migration from the three systems it replaces (DESIGN §9):
 * ui/tutorial.ts (`tutorialStep`), ui/hub/coach.ts (`coachStep`) and the Armoury's gate coach (`gearGateSeen`). A
 * returning player is marked as already knowing what their progress shows they have met, so they are not re-taught.
 */

/** coach.ts COACH_STEPS.indexOf("done"): the index the legacy mirror writes once the hub's first session is over. */
export const COACH_DONE = 4;
/** tutorial.ts TUTORIAL_DONE. */
export const TUTORIAL_DONE = -1;
/** coach.ts COACH_STEPS indices: equip, upgrade and play2 reached. */
const COACH_EQUIP = 1, COACH_UPGRADE = 2, COACH_PLAY2 = 3;
/** tutorial.ts step 4 ("Ready. Tap START WAVES"): reaching it means the draft was done. */
const TUTORIAL_READY_STEP = 4;
/** Levels cleared past which the hub's first session is over, the Armoury's gate has been met, and the in-level
 * basics (upgrade cards, reroll, skip, map controls, the rally flag) are known. */
const HC_FIRST_SESSION_DONE = 2;
const HC_BASICS_KNOWN = 3;
const HC_GEAR_GATE_MET = 5;

/** The level-1 walkthrough's ids (DESIGN §6.2, scripts/level1.ts), in script order: the welcome card, then the draft. */
export const L1_STEP_IDS = [
  "l1.welcome", "l1.fortress", "l1.pickRoad", "l1.placeRoad", "l1.moreRoad", "l1.portal", "l1.pickTower", "l1.chooseCell",
  "l1.placeTower", "l1.secondTower", "l1.start", "l1.watch", "l1.speed", "l1.points", "l1.pickOffer", "l1.break",
  "l1.towerTap",
] as const;
/** The draft part, up to START WAVES: done for a player who quit level 1 after starting its waves. */
const L1_THROUGH_START = L1_STEP_IDS.slice(0, L1_STEP_IDS.indexOf("l1.start") + 1);
/** Level-1 lines that are tips rather than draft steps. */
const L1_TIPS = ["l1.watch", "l1.speed", "l1.towerTap"] as const;

/**
 * Retired ids, reserved (never reused): the hub's welcome card and its PLAY lesson. A brand-new player now goes from
 * the title straight into level 1, where the welcome card opens the walkthrough (l1.welcome). Every save carries them
 * as done, so a rollback build does not welcome anyone in the hub twice.
 */
export const RETIRED_STEP_IDS = ["hub.welcome", "hub.play1"] as const;

/** The hub's first session, in order (DESIGN §6.1), from the first win on: quiet() stays false until the last is done. */
export const HUB_FIRST_SESSION = [
  "hub.firstWin", "hub.equip", "hub.equipBest", "hub.upgrade", "hub.upgradeGo",
  "hub.upgradeWatch", "hub.play2",
] as const;

export function emptyGuideSave(): GuideSaveJson { return { v: GUIDE_SAVE_VERSION, done: [] }; }

/** A clean copy: known version, string ids without repeats, flags only when set. */
function normalize(s: GuideSaveJson): GuideSaveJson {
  const out: GuideSaveJson = { v: GUIDE_SAVE_VERSION, done: [...new Set(s.done.filter((x) => typeof x === "string"))] };
  if (s.tipsOff === true) out.tipsOff = true;
  if (s.walkthroughSkipped === true) out.walkthroughSkipped = true;
  return out;
}

/**
 * The guide's save at boot: the stored one when present and well formed, else migrated from the legacy fields
 * (`migrated` then tells the controller to persist it once).
 */
export function loadGuideSave(saved: GuideSaveJson | undefined, legacy: GuideLegacySave, unlocks: GuideCreateOptions["unlocks"]): { save: GuideSaveJson; migrated: boolean } {
  if (saved && Number.isInteger(saved.v) && saved.v >= GUIDE_SAVE_VERSION && Array.isArray(saved.done)) {
    const save = normalize(saved);
    return { save, migrated: retireHubWelcome(save) };
  }
  return { save: migrateGuide(legacy, unlocks), migrated: true };
}

/**
 * The welcome moved from the hub to level 1 (2026-09-24): the retired hub ids are marked done, and a player the hub
 * already welcomed is not welcomed again in level 1. True when it changed the save (the controller then persists it).
 *
 * "Already welcomed" is read as hub.welcome without hub.play1 (welcomed, PLAY not yet tapped), or any level-1 step
 * done (past the welcome anyway). Both hub ids alone cannot tell a welcomed player from a save this function already
 * retired, so a player who tapped PLAY on an older build and never reached the fortress line is welcomed once more.
 */
export function retireHubWelcome(save: GuideSaveJson): boolean {
  const done = new Set(save.done);
  const welcomed = (done.has("hub.welcome") && !done.has("hub.play1")) || L1_STEP_IDS.some((id) => done.has(id));
  const add = [...(welcomed ? ["l1.welcome"] : []), ...RETIRED_STEP_IDS].filter((id) => !done.has(id));
  save.done.push(...add);
  return add.length > 0;
}

/** DESIGN §9's table, row by row. `hub.guideIntro` is never marked: every returning player learns where the Guide is. */
export function migrateGuide(legacy: GuideLegacySave, unlocks: GuideCreateOptions["unlocks"]): GuideSaveJson {
  const hc = Math.max(0, legacy.highestLevelCleared | 0);
  const t = legacy.tutorialStep ?? 0;
  const coach = legacy.coachStep ?? 0;
  const done = new Set<string>();
  const mark = (...ids: readonly string[]) => { for (const id of ids) done.add(id); };
  let walkthroughSkipped = false;

  if (hc >= 1 || t === TUTORIAL_DONE) mark(...L1_STEP_IDS, "hub.welcome", "hub.play1");
  if (hc === 0 && t === TUTORIAL_DONE) walkthroughSkipped = true;
  if (hc === 0 && t >= TUTORIAL_READY_STEP) mark("hub.welcome", "hub.play1", ...L1_THROUGH_START);
  if (hc === 0 && t >= 1 && t < TUTORIAL_READY_STEP) mark("hub.welcome", "hub.play1", "l1.fortress");

  const coachAll = coach >= COACH_DONE || hc >= HC_FIRST_SESSION_DONE;
  if (coach >= COACH_EQUIP || coachAll) mark("hub.firstWin");
  if (coach >= COACH_UPGRADE || coachAll) mark("hub.equip", "hub.equipBest");
  if (coach >= COACH_PLAY2 || coachAll) mark("hub.upgrade", "hub.upgradeGo", "hub.upgradeWatch");
  if (coachAll) mark(...HUB_FIRST_SESSION);

  if (legacy.gearGateSeen === true || hc >= HC_GEAR_GATE_MET) mark("hub.gearGate", "hub.gearGateEquip");
  for (const e of unlocks.enemies) {
    if (e.unlockLevel > hc) continue;
    mark(`hint.enemy.${e.id}`);
    if (e.air) mark("hint.flyers");
  }
  if (hc >= 1) mark("hint.firstBoss", "end.firstWin");
  for (const tw of unlocks.towers) if (tw.unlockLevel > 1 && tw.unlockLevel <= hc + 1) mark(`hint.newTower.${tw.id}`, `hub.newTower.${tw.id}`);
  if (hc >= HC_BASICS_KNOWN) mark("hint.upgradeCard", "hint.reroll", "hint.skip", "hint.mapControls", "hint.rally", "hub.stars", "hub.orders", ...L1_TIPS);

  const out: GuideSaveJson = { v: GUIDE_SAVE_VERSION, done: [...done] };
  if (walkthroughSkipped) out.walkthroughSkipped = true;
  retireHubWelcome(out);
  return out;
}

/** The legacy fields as the guide's save implies them (DESIGN §9 "Legacy fields"); only fields to raise are set. */
export interface LegacyMirror { tutorialStep?: number; coachStep?: number; gearGateSeen?: boolean }

/**
 * For one release the guide mirrors its progress into the old fields, so a rollback build does not re-tutor anyone:
 * `tutorialStep = −1` once `l1.start` is done or the walkthrough skipped, `coachStep = COACH_DONE` once `hub.play2` is
 * done, `gearGateSeen` with `hub.gearGate`. It only ever raises a field, never lowers one.
 */
export function legacyMirror(save: GuideSaveJson, legacy: GuideLegacySave): LegacyMirror {
  const done = new Set(save.done);
  const out: LegacyMirror = {};
  if ((done.has("l1.start") || save.walkthroughSkipped === true) && legacy.tutorialStep !== TUTORIAL_DONE) out.tutorialStep = TUTORIAL_DONE;
  if (done.has("hub.play2") && (legacy.coachStep ?? 0) < COACH_DONE) out.coachStep = COACH_DONE;
  if (done.has("hub.gearGate") && legacy.gearGateSeen !== true) out.gearGateSeen = true;
  return out;
}

/**
 * Writes the mirror onto the legacy record. main.ts passes the live SaveRecord as `legacy` and saves the whole record
 * in `persist`, so writing here lands the mirrored fields in the same save.
 */
export function applyLegacyMirror(legacy: GuideLegacySave, save: GuideSaveJson): void {
  Object.assign(legacy as LegacyMirror, legacyMirror(save, legacy));
}
